import {
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  GetAppRounded,
  MoreVertRounded,
  VisibilityRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: {
      margin: theme.spacing(1, 0)
    },
    fullList: {
      width: 'auto'
    },
    cover: {
      margin: theme.spacing(1, 0),
      width: '100%',
      display: 'flex'
    },
    listHeader: {
      margin: theme.spacing(-1, 0)
    },
    filename: {
      wordBreak: 'break-all',
      lineHeight: '120%',
      marginBottom: theme.spacing(0.25)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexAlignRight: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    flexGrow: {
      flexGrow: 1
    },
    marginRight: {
      marginRight: theme.spacing(1)
    }
  })
);

interface DocumentCoversheetFileProps {
  coversheetName: string;
  onPreview: () => void;
  onDownload: () => void;
}

export default function DocumentCoversheetFile({
  coversheetName,
  onPreview,
  onDownload
}: DocumentCoversheetFileProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [openCoversheet, setOpenCoversheet] = useState<boolean>(false);

  function toggleDrawer(open: boolean) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpenCoversheet(open);
    };
  }

  return (
    <div>
      <Drawer
        anchor="bottom"
        open={openCoversheet}
        onClose={toggleDrawer(false)}
      >
        <div
          className={classes.fullList}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem className={classes.listHeader}>
              <ListItemText
                primary={coversheetName}
                secondary={t('documentCoversheetFile.coversheetAutoGenerated') as string}
              />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem onClick={onPreview} button>
              <ListItemIcon>
                <VisibilityRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentCoversheetFile.preview') as string} />
            </ListItem>
            <ListItem onClick={onDownload} button>
              <ListItemIcon>
                <GetAppRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentCoversheetFile.download') as string} />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <div className={clsx(classes.cover, classes.flexAlignCenter)}>
        <div className={clsx(classes.flexAlignCenter, classes.flexGrow)}>
          <Tooltip title={t('documentCoversheetFile.download') as string} arrow>
            <IconButton
              onClick={onDownload}
              className={classes.marginRight}
              size="small"
              edge="start"
            >
              <GetAppRounded />
            </IconButton>
          </Tooltip>
          <div>
            <Typography className={classes.filename} variant="body1">
              {coversheetName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t('documentCoversheetFile.coversheetAutoGenerated') as string}
            </Typography>
          </div>
        </div>
        <div className={classes.flexAlignRight}>
          <IconButton size="small" onClick={toggleDrawer(true)}>
            <MoreVertRounded />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
